<script setup lang="ts">
const props = defineProps<{
  imgs: string[];
  showPlaceholder?: boolean;
}>();

const midiStore = useMidiStore();
const fs = useFullScreenStore();
watch(props, () => updateSVG(), { deep: true });
const svgholder = ref<HTMLDivElement>();

function updateSVG() {
  if (!svgholder.value) {
    return;
  }

  svgholder.value.innerHTML = "";

  if (!props.imgs.length) {
    return;
  }

  const svgs = props.imgs.map((i) => {
    const string = i.replace("data:image/svg+xml;base64, ", "");
    return decodeURIComponent(escape(window.atob(string)));
  });

  svgs.forEach((svg, i) => {
    const element = document.createElement("div");
    element.classList.add("svg-div");
    element.classList.add("musicpage");
    element.classList.add(`page-${i + 1}`);
    element.setAttribute("data-page", (i + 1).toString());
    element.innerHTML = svg;
    svgholder.value!.appendChild(element);
  });

  svgholder.value.querySelectorAll(".note").forEach((element) => {
    element.addEventListener("click", (e: Event) => {
      midiStore.noteclick(element.id);
      const offsetTop = element.getBoundingClientRect().top + window.scrollY;

      const scrollPosition =
        offsetTop -
        (window.innerHeight - element.getBoundingClientRect().height) / 2;

      if (fs.fullScreenEnabled) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else {
        window.scrollTo({
          top: scrollPosition,
          behavior: "smooth",
        });
      }
      element.classList.add("highlight-active");
    });
  });
}

onMounted(async () => {
  await nextTick();
  updateSVG();
});
</script>

<template>
  <div class="wrapper sheetmusic-wrapper">
    <div
      ref="svgholder"
      class="svg-holder"
      :class="{ show: !showPlaceholder }"
    ></div>
    <div v-if="showPlaceholder">
      <svg
        id="Layer_1"
        class="sheetmusic-placeholder"
        version="1.1"
        xmlns:mei="http://www.music-encoding.org/ns/mei"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
        x="0px"
        y="0px"
        viewBox="0 0 2100 2970"
        style="enable-background: new 0 0 2100 2970"
        xml:space="preserve"
      >
        <g id="mh13c0s"></g>
        <g id="s1j3m0rx"></g>
        <g id="sew3g8i"></g>
        <g id="pqufuqo"></g>
        <g id="s2b46lt"></g>
        <g id="sw8605b"></g>
        <g id="s4odbhf"></g>
        <g id="s13bi9a8"></g>
        <g id="s1oa9pyt"></g>
        <rect
          x="938.8"
          y="2768.6"
          class="st0"
          width="226.9"
          height="26.3"
        />
        <rect
          x="615.4"
          y="2844.6"
          class="st0"
          width="873.6"
          height="26.3"
        />
        <rect
          x="484.1"
          y="2806.6"
          class="st0"
          width="1136.2"
          height="26.3"
        />
        <rect
          x="917"
          y="2882.6"
          class="st0"
          width="270.4"
          height="26.3"
        />
        <rect
          x="1666.9"
          y="216.4"
          class="st0"
          width="337.6"
          height="33"
        />
        <rect
          x="1785.9"
          y="257.9"
          class="st0"
          width="218.6"
          height="33"
        />
        <rect
          x="1785.9"
          y="174.9"
          class="st0"
          width="218.6"
          height="33"
        />
        <rect
          x="606.4"
          y="103.6"
          class="st0"
          width="968.8"
          height="89"
        />
        <rect
          x="236.8"
          y="342.5"
          class="st0"
          width="259.2"
          height="54.1"
        />
        <g>
          <rect
            x="167.1"
            y="2570.4"
            class="st0"
            width="523.5"
            height="41.7"
          />
          <rect
            x="801.8"
            y="2570.4"
            class="st0"
            width="533.9"
            height="41.7"
          />
          <rect
            x="1410.7"
            y="2573.3"
            class="st0"
            width="580.4"
            height="41.7"
          />
          <rect
            x="140.8"
            y="2402.3"
            class="st0"
            width="57"
            height="41.7"
          />
          <rect
            x="1422.5"
            y="2413.3"
            class="st0"
            width="59.8"
            height="41.7"
          />
          <rect
            x="107.3"
            y="2464.3"
            class="st0"
            width="1889.8"
            height="84.7"
          />
        </g>
        <g>
          <rect
            x="167.1"
            y="2290.4"
            class="st0"
            width="523.5"
            height="41.7"
          />
          <rect
            x="801.8"
            y="2290.4"
            class="st0"
            width="533.9"
            height="41.7"
          />
          <rect
            x="1410.7"
            y="2293.3"
            class="st0"
            width="580.4"
            height="41.7"
          />
          <rect
            x="140.8"
            y="2122.3"
            class="st0"
            width="57"
            height="41.7"
          />
          <rect
            x="1422.5"
            y="2133.3"
            class="st0"
            width="59.8"
            height="41.7"
          />
          <rect
            x="107.3"
            y="2184.3"
            class="st0"
            width="1889.8"
            height="84.7"
          />
        </g>
        <g>
          <rect
            x="167.1"
            y="2010.3"
            class="st0"
            width="523.5"
            height="41.7"
          />
          <rect
            x="801.8"
            y="2010.3"
            class="st0"
            width="533.9"
            height="41.7"
          />
          <rect
            x="1410.7"
            y="2013.2"
            class="st0"
            width="580.4"
            height="41.7"
          />
          <rect
            x="140.8"
            y="1842.2"
            class="st0"
            width="57"
            height="41.7"
          />
          <rect
            x="1422.5"
            y="1853.2"
            class="st0"
            width="59.8"
            height="41.7"
          />
          <rect
            x="107.3"
            y="1904.2"
            class="st0"
            width="1889.8"
            height="84.7"
          />
        </g>
        <g>
          <rect
            x="167.1"
            y="1730.3"
            class="st0"
            width="523.5"
            height="41.7"
          />
          <rect
            x="801.8"
            y="1730.3"
            class="st0"
            width="533.9"
            height="41.7"
          />
          <rect
            x="1410.7"
            y="1733.2"
            class="st0"
            width="580.4"
            height="41.7"
          />
          <rect
            x="140.8"
            y="1562.2"
            class="st0"
            width="57"
            height="41.7"
          />
          <rect
            x="1422.5"
            y="1573.2"
            class="st0"
            width="59.8"
            height="41.7"
          />
          <rect
            x="107.3"
            y="1624.2"
            class="st0"
            width="1889.8"
            height="84.7"
          />
        </g>
        <g>
          <rect
            x="167.1"
            y="890.2"
            class="st0"
            width="523.5"
            height="41.7"
          />
          <rect
            x="801.8"
            y="890.2"
            class="st0"
            width="533.9"
            height="41.7"
          />
          <rect
            x="1410.7"
            y="893.1"
            class="st0"
            width="580.4"
            height="41.7"
          />
          <rect
            x="140.8"
            y="722.1"
            class="st0"
            width="57"
            height="41.7"
          />
          <rect
            x="1422.5"
            y="733.1"
            class="st0"
            width="59.8"
            height="41.7"
          />
          <rect
            x="107.3"
            y="784.1"
            class="st0"
            width="1889.8"
            height="84.7"
          />
        </g>
        <g>
          <rect
            x="167.1"
            y="1170.2"
            class="st0"
            width="523.5"
            height="41.7"
          />
          <rect
            x="801.8"
            y="1170.2"
            class="st0"
            width="533.9"
            height="41.7"
          />
          <rect
            x="1410.7"
            y="1173.1"
            class="st0"
            width="580.4"
            height="41.7"
          />
          <rect
            x="140.8"
            y="1002.1"
            class="st0"
            width="57"
            height="41.7"
          />
          <rect
            x="1422.5"
            y="1013.1"
            class="st0"
            width="59.8"
            height="41.7"
          />
          <rect
            x="107.3"
            y="1064.1"
            class="st0"
            width="1889.8"
            height="84.7"
          />
        </g>
        <g>
          <rect
            x="292.5"
            y="610.1"
            class="st0"
            width="487.6"
            height="41.7"
          />
          <rect
            x="883.7"
            y="610.1"
            class="st0"
            width="497.3"
            height="41.7"
          />
          <rect
            x="1450.9"
            y="613"
            class="st0"
            width="540.6"
            height="41.7"
          />
          <rect
            x="268"
            y="442"
            class="st0"
            width="53.1"
            height="41.7"
          />
          <rect
            x="1461.9"
            y="453"
            class="st0"
            width="55.7"
            height="41.7"
          />
          <rect
            x="236.8"
            y="504"
            class="st0"
            width="1760.4"
            height="84.7"
          />
        </g>
        <g>
          <rect
            x="167.1"
            y="1450.2"
            class="st0"
            width="523.5"
            height="41.7"
          />
          <rect
            x="801.8"
            y="1450.2"
            class="st0"
            width="533.9"
            height="41.7"
          />
          <rect
            x="1410.7"
            y="1453.1"
            class="st0"
            width="580.4"
            height="41.7"
          />
          <rect
            x="140.8"
            y="1282.1"
            class="st0"
            width="57"
            height="41.7"
          />
          <rect
            x="1422.5"
            y="1293.1"
            class="st0"
            width="59.8"
            height="41.7"
          />
          <rect
            x="107.3"
            y="1344.1"
            class="st0"
            width="1889.8"
            height="84.7"
          />
        </g>
      </svg>
    </div>
    <!-- <Image
      src="https://cdn.ccli.com/songselect/vue/placeholder-lead.svg"
      alt="Placeholder image for lead sheet music"
      transparent
    /> -->
  </div>
</template>

<style scoped lang="postcss">
.svg-holder {
  @apply hidden;

  &.show {
    @apply block;
  }
}
.wrapper:deep() {
  svg,
  img {
    @apply mb-3 h-auto w-full bg-white;
  }
  .highlight-active {
    fill: red;
  }
  .note {
    pointer-events: bounding-box !important;
    cursor: pointer;
  }
  .note:hover {
    fill: red;
  }

  tspan.text:empty {
    display: none;
  }

  g.notehead:hover,
  g.harm:hover {
    fill: unset !important;
  }
}
.wrapper {
  /* @apply bg-gray-100 rounded-lg p-4;*/
  width: 100%;
  @media screen(max-md) {
    @apply bg-transparent p-0;
  }
  :deep() {
    svg,
    img {
      @apply mb-0 block;
    }
  }
}
.sheetmusic-placeholder {
  rect {
    @apply fill-black opacity-10;
  }
}
</style>
